(() => {
  const initCommissionPage = (root) => {
    if (!root || root.dataset.ebcReady === 'true') return;
    root.dataset.ebcReady = 'true';

    const progress = root.querySelector('[data-ebc-progress]');
    const updateProgress = () => {
      if (!progress) return;
      const rect = root.getBoundingClientRect();
      const scrollable = Math.max(root.offsetHeight - window.innerHeight, 1);
      const travelled = Math.min(Math.max(-rect.top, 0), scrollable);
      progress.style.transform = `scaleX(${travelled / scrollable})`;
    };

    const revealItems = root.querySelectorAll('.ebc-reveal');
    if (window.location.hash) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    } else if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
      revealItems.forEach((item) => observer.observe(item));
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    const pricing = root.querySelector('[data-ebc-pricing]');
    const priceButton = root.querySelector('[data-ebc-price-button]');
    const particles = root.querySelector('[data-ebc-price-particles]');

    const playGlitterChime = () => {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const context = new AudioContext();
      const master = context.createGain();
      master.gain.setValueAtTime(0.0001, context.currentTime);
      master.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.025);
      master.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 1.05);
      master.connect(context.destination);

      [1046.5, 1318.5, 1568, 2093].forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const start = context.currentTime + index * 0.075;
        oscillator.type = index % 2 ? 'sine' : 'triangle';
        oscillator.frequency.setValueAtTime(frequency, start);
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.17 / (index + 1), start + 0.018);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.58);
        oscillator.connect(gain);
        gain.connect(master);
        oscillator.start(start);
        oscillator.stop(start + 0.62);
      });

      window.setTimeout(() => context.close(), 1300);
    };

    if (pricing && priceButton) {
      pricing.classList.add('is-price-locked');
      priceButton.addEventListener('click', () => {
        if (pricing.classList.contains('is-price-revealing')) return;
        pricing.classList.add('is-price-revealing');
        priceButton.setAttribute('aria-expanded', 'true');
        priceButton.disabled = true;
        playGlitterChime();

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (particles && !reducedMotion) {
          const colors = ['#a87443', '#d7b37e', '#f6f1e8', '#8b5f36'];
          for (let index = 0; index < 46; index += 1) {
            const particle = document.createElement('i');
            const angle = Math.random() * Math.PI * 2;
            const distance = 95 + Math.random() * 190;
            particle.className = 'ebc-price-particle';
            particle.style.setProperty('--x', `${Math.cos(angle) * distance}px`);
            particle.style.setProperty('--y', `${Math.sin(angle) * distance}px`);
            particle.style.setProperty('--delay', `${Math.random() * 150}ms`);
            particle.style.setProperty('--size', `${2 + Math.random() * 5}px`);
            particle.style.setProperty('--particle-color', colors[index % colors.length]);
            particles.appendChild(particle);
          }
        }

        window.setTimeout(() => {
          pricing.classList.remove('is-price-locked');
          pricing.classList.add('is-price-revealed');
        }, reducedMotion ? 100 : 560);

        window.setTimeout(() => {
          pricing.classList.remove('is-price-revealing');
          if (particles) particles.replaceChildren();
        }, 1400);
      });
    }

    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);

    const quickbar = root.querySelector('[data-ebc-quickbar]');
    const firstStoryEnd = root.querySelector('[id^="ebc-first-story-end-"]');
    const reserveSection = root.querySelector('[id^="reserve-commission-"]');
    const quickbarClose = root.querySelector('[data-ebc-quickbar-close]');
    const dismissKey = 'ebc-quickbar-dismissed';

    if (quickbar && firstStoryEnd && 'IntersectionObserver' in window) {
      let dismissed = false;
      try {
        dismissed = window.sessionStorage.getItem(dismissKey) === 'true';
      } catch (e) {
        dismissed = false;
      }

      if (!dismissed) {
        const showObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
                quickbar.classList.add('is-visible');
                showObserver.unobserve(entry.target);
              }
            });
          },
          { threshold: 0 }
        );
        showObserver.observe(firstStoryEnd);

        if (reserveSection) {
          const hideObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  quickbar.classList.remove('is-visible');
                }
              });
            },
            { threshold: 0.15 }
          );
          hideObserver.observe(reserveSection);
        }
      }

      if (quickbarClose) {
        quickbarClose.addEventListener('click', () => {
          quickbar.classList.remove('is-visible');
          try {
            window.sessionStorage.setItem(dismissKey, 'true');
          } catch (e) {
            /* sessionStorage unavailable, dismissal just won't persist */
          }
        });
      }
    }

    const configurator = root.querySelector('[data-ebc-configurator]');
    if (configurator) {
      const form = configurator.closest('form');
      const isWeb3form = !!(form && form.hasAttribute('data-ebc-web3form'));
      const steps = Array.from(configurator.querySelectorAll('[data-ebc-step]'));
      const dots = Array.from(configurator.querySelectorAll('.ebc-configurator__progress span'));
      const summaryText = configurator.querySelector('[data-ebc-summary-text]');
      const summaryField = root.querySelector('[data-ebc-summary-field]');
      const fileField = configurator.querySelector('[data-ebc-file-field]');
      const fileInput = fileField ? fileField.querySelector('input[type="file"]') : null;
      const uploadLabel = configurator.querySelector('[data-ebc-upload-label]');
      const answers = {};

      const goToStep = (index) => {
        steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
          dot.classList.toggle('is-done', i < index);
        });
        if (index === steps.length - 1) {
          buildSummary();
          const firstField = steps[index].querySelector('input[type="text"], input[type="email"]');
          if (firstField) firstField.focus({ preventScroll: true });
        }
      };

      const buildSummary = () => {
        const parts = [answers.subject, answers.era, answers.base, answers.size].filter(Boolean);
        if (summaryText) {
          summaryText.textContent = parts.length
            ? `${parts.join(', ')}. Got it.`
            : '';
        }
        if (summaryField) {
          summaryField.value = [
            answers.subject ? `Subject: ${answers.subject}` : null,
            answers.era ? `Portrayal: ${answers.era}` : null,
            answers.base ? `Base: ${answers.base}` : null,
            answers.size ? `Size: ${answers.size}` : null,
            answers.hasPhotos ? `Has photos to send: ${answers.hasPhotos}` : null,
          ].filter(Boolean).join('\n');
        }
      };

      configurator.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener('change', () => {
          const stepEl = input.closest('[data-ebc-step]');
          const stepIndex = steps.indexOf(stepEl);
          if (input.name === 'ebc_subject') answers.subject = input.value;
          if (input.name === 'ebc_era') answers.era = input.value;
          if (input.name === 'ebc_base') answers.base = input.value;
          if (input.name === 'ebc_size') answers.size = input.value;
          if (input.hasAttribute('data-ebc-has-photos')) {
            answers.hasPhotos = input.value;
            if (fileField) {
              const showFile = input.value === 'yes';
              fileField.hidden = !showFile;
              if (!showFile && fileInput) fileInput.value = '';
            }
          }

          window.setTimeout(() => {
            if (stepIndex > -1 && stepIndex < steps.length - 1) {
              goToStep(stepIndex + 1);
            }
          }, 260);
        });
      });

      if (fileInput && uploadLabel) {
        fileInput.addEventListener('change', () => {
          const count = fileInput.files ? fileInput.files.length : 0;
          uploadLabel.textContent = count === 0
            ? 'Choose photos'
            : count === 1
              ? '1 photo selected'
              : `${count} photos selected`;
        });
      }

      if (isWeb3form && form) {
        const successEl = form.querySelector('[data-ebc-web3form-success]');
        const errorEl = form.querySelector('[data-ebc-web3form-error]');
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          if (errorEl) errorEl.hidden = true;
          const submitBtn = form.querySelector('button[type="submit"]');
          if (submitBtn) submitBtn.disabled = true;

          fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'application/json' },
          })
            .then((response) => response.json().catch(() => ({})).then((data) => ({ ok: response.ok, data })))
            .then(({ ok }) => {
              if (!ok) throw new Error('submit failed');
              configurator.hidden = true;
              if (successEl) successEl.hidden = false;
            })
            .catch(() => {
              if (errorEl) errorEl.hidden = false;
              if (submitBtn) submitBtn.disabled = false;
            });
        });
      }

      goToStep(0);
    }
  };

  const initAll = () => {
    document.querySelectorAll('[data-ebc-page]').forEach(initCommissionPage);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initCommissionPage(event.target.querySelector('[data-ebc-page]'));
  });
})();
